import {
  type ParentComponent,
  createContext,
  Show,
  createMemo,
  createSignal,
  useContext,
} from 'solid-js';
import { useLocation, useNavigate } from '@solidjs/router';
import { useMutation, useQueryClient } from '@tanstack/solid-query';
import {
  prizeDestinationOf,
  tournamentApi,
  type UpdateTournamentRequest,
} from '~/shared/api/tournament-api';
import {
  PrizeDestination,
  type TournamentDetailDto,
  TournamentStatus,
  type UpdateTournamentBody,
} from '~/shared/api/generated';
import { CreateTournamentDialog, type EditTerms } from '~/widgets/schedule/CreateTournamentDialog';
import { ResetBracketDialog } from '~/widgets/tournaments/ResetBracketDialog';
import type { TournamentDraft } from '~/features/schedule/schedule-store';
import { useCan } from '~/shared/access/useCan';
import { useToast } from '~/shared/ui/Toast';
import { haptic } from '~/shared/lib/haptics';
import { ApiError } from '~/shared/api/http-client';
import { queryKeys } from '~/shared/api/query-keys';

export const TOURNAMENT_OPEN_CREATE_EVENT = 'tournament:open-create';

export const CREATE_TOURNAMENT_QUERY = 'createTournament';

export const TOURNAMENT_RESUME_KEY = 'sportpark:tournament-resume';

export interface TournamentPanelContextValue {
  openCreate: () => void;
  openEdit: (tournament: TournamentDetailDto) => void;
  closeEdit: () => void;
}

const TournamentPanelContext = createContext<TournamentPanelContextValue>();

const tournamentToDraft = (t: TournamentDetailDto): TournamentDraft => {
  const dateStr = t.startAt.slice(0, 10);
  const hhmm = (iso: string): string => iso.slice(11, 16);
  return {
    name: t.name,
    sport: t.sport,
    format: t.format,
    scoring: t.scoring,
    expectedParticipants: t.expectedParticipants,
    minPlayers: t.minPlayers,
    maxPlayers: t.maxPlayers,
    date: dateStr,
    startTime: hhmm(t.startAt),
    endTime: hhmm(t.endAt),
    note: t.note ?? '',
    registrationOpensAt: t.registrationOpensAt ?? null,
    registrationClosesAt: t.registrationClosesAt ?? null,
    mode: 'manual',
    entryFeeKopecks: t.entryFeeKopecks,
    prizePoolKopecks: t.prizePoolKopecks,
    prizeDistribution: t.prizeDistribution,
    pointsPerMatch: t.pointsPerMatch,
    levelMin: t.levelMin ?? null,
    levelMax: t.levelMax ?? null,
    hideParticipants: t.hideParticipants ?? false,
    prizeDestination: t.prizeDestination ?? 'advance',
    awardPointsAsBonuses: false,
    gradientFrom: t.gradientFrom ?? '',
    gradientTo: t.gradientTo ?? '',
  };
};

const draftToUpdate = (
  draft: TournamentDraft,
  original: TournamentDetailDto,
): UpdateTournamentRequest => {
  const payload: UpdateTournamentRequest = {};
  if (draft.name !== original.name) payload.name = draft.name;
  const noteNext = draft.note.length > 0 ? draft.note : null;
  if (noteNext !== (original.note ?? null)) payload.note = noteNext;
  if (draft.entryFeeKopecks !== original.entryFeeKopecks) payload.entryFeeKopecks = draft.entryFeeKopecks;
  if (draft.prizePoolKopecks !== original.prizePoolKopecks) payload.prizePoolKopecks = draft.prizePoolKopecks;
  if (draft.pointsPerMatch !== original.pointsPerMatch)
    payload.pointsPerMatch = draft.pointsPerMatch;
  if (draft.maxPlayers !== original.maxPlayers) payload.maxPlayers = draft.maxPlayers;
  if (draft.scoring !== original.scoring) payload.scoring = draft.scoring;
  if (draft.minPlayers !== original.minPlayers) payload.minPlayers = draft.minPlayers;
  const regOpensNext = draft.registrationOpensAt ?? null;
  if (regOpensNext !== (original.registrationOpensAt ?? null))
    payload.registrationOpensAt = regOpensNext;
  const regClosesNext = draft.registrationClosesAt ?? null;
  if (regClosesNext !== (original.registrationClosesAt ?? null))
    payload.registrationClosesAt = regClosesNext;
  const samePrizes =
    draft.prizeDistribution.length === original.prizeDistribution.length &&
    draft.prizeDistribution.every((p, i) => {
      const o = original.prizeDistribution[i];
      return o && o.place === p.place && o.percent === p.percent;
    });
  if (!samePrizes) payload.prizeDistribution = draft.prizeDistribution;
  const levelMinNext = draft.levelMin ?? null;
  const levelMaxNext = draft.levelMax ?? null;
  const levelChanged =
    levelMinNext !== (original.levelMin ?? null) ||
    levelMaxNext !== (original.levelMax ?? null);
  if (levelChanged) {
    payload.level = { min: levelMinNext, max: levelMaxNext };
  }
  if (draft.hideParticipants !== (original.hideParticipants ?? false))
    payload.hideParticipants = draft.hideParticipants;
  if (draft.prizeDestination !== (original.prizeDestination ?? 'advance'))
    payload.prizeDestination = prizeDestinationOf(draft.prizeDestination);
  const gradFromNext = draft.gradientFrom.length > 0 ? draft.gradientFrom : null;
  const gradToNext = draft.gradientTo.length > 0 ? draft.gradientTo : null;
  if (gradFromNext !== (original.gradientFrom ?? null)) payload.gradientFrom = gradFromNext;
  if (gradToNext !== (original.gradientTo ?? null)) payload.gradientTo = gradToNext;
  return payload;
};

const TERMS_OPEN: ReadonlySet<TournamentStatus> = new Set([
  TournamentStatus.DRAFT,
  TournamentStatus.REGISTRATION_OPEN,
  TournamentStatus.REGISTRATION_CLOSED,
]);

export const TERMS_LOCKED_TEXT =
  'Сетка уже сформирована: окно, корты и участники меняются только до неё — сначала расформируйте сетку.';

const editTermsOf = (t: TournamentDetailDto, onResetBracket: () => void): EditTerms => {
  if (TERMS_OPEN.has(t.status)) return { courtIds: t.courtIds, locked: null };
  return {
    courtIds: t.courtIds,
    locked: TERMS_LOCKED_TEXT,
    resetRefusal: t.bracketResettable ? null : (t.bracketResetRefusal ?? null),
    onResetBracket: t.status === TournamentStatus.IN_PROGRESS && t.bracketResettable ? onResetBracket : undefined,
  };
};

const basicsOf = ({ status: _status, prizeDestination, ...rest }: UpdateTournamentRequest): UpdateTournamentBody => ({
  ...rest,
  ...(prizeDestination
    ? { prizeDestination: prizeDestination === 'bonus' ? PrizeDestination.BONUS : PrizeDestination.ADVANCE }
    : {}),
});

export const TournamentPanelProvider: ParentComponent = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = createSignal<TournamentDetailDto | null>(null);
  const [resetting, setResetting] = createSignal<TournamentDetailDto | null>(null);
  const canManage = useCan('/tournaments', 'manage');

  const [creating, setCreating] = createSignal(false);

  const openCreate = (): void => {
    if (location.pathname === '/schedule') {
      window.dispatchEvent(new CustomEvent(TOURNAMENT_OPEN_CREATE_EVENT));
      return;
    }
    setCreating(true);
  };

  const onCreateProceed = (draft: TournamentDraft, mode: 'auto' | 'manual'): void => {
    try {
      sessionStorage.setItem(
        TOURNAMENT_RESUME_KEY,
        JSON.stringify({ draft, mode }),
      );
    } catch {
    }
    setCreating(false);
    navigate(`/schedule?${CREATE_TOURNAMENT_QUERY}=resume&date=${draft.date}`);
  };

  const editTerms = createMemo((): EditTerms | null => {
    const t = editing();
    if (!t) return null;
    return editTermsOf(t, () => {
      setResetting(t);
      setEditing(null);
    });
  });

  const openEdit = (tournament: TournamentDetailDto): void => {
    if (!canManage()) return;
    setEditing(tournament);
  };

  const closeEdit = (): void => {
    if (submitEditMut.isPending) return;
    setEditing(null);
  };

  const submitEditMut = useMutation(() => ({
    mutationFn: async (args: {
      current: TournamentDetailDto;
      draft: TournamentDraft;
      courtIds: string[];
      payload: ReturnType<typeof draftToUpdate>;
      viaReschedule: boolean;
    }) => {
      if (!args.viaReschedule) {
        await tournamentApi.update(args.current.id, args.payload);
        return;
      }
      await tournamentApi.reschedule(args.current.id, {
        startAt: `${args.draft.date}T${args.draft.startTime}:00+00:00`,
        endAt: `${args.draft.date}T${args.draft.endTime}:00+00:00`,
        resourceIds: args.courtIds,
        expectedParticipants: args.draft.expectedParticipants,
        ...(Object.keys(args.payload).length > 0 ? { basics: basicsOf(args.payload) } : {}),
      });
    },
    onSuccess: (_data, args) => {
      if (args.viaReschedule) {
        queryClient.invalidateQueries({ queryKey: ['schedule'] });
        queryClient.invalidateQueries({ queryKey: ['schedule-range'] });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.tournament.detail(args.current.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournament.all() });
      toast.success('Параметры турнира обновлены');
      haptic('success');
      setEditing(null);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Не удалось сохранить');
      haptic('error');
    },
  }));

  const submitEdit = (draft: TournamentDraft, courtIds: string[]): void => {
    const current = editing();
    if (!current || !canManage()) return;
    const payload = draftToUpdate(draft, current);

    const sameCourts =
      courtIds.length === current.courtIds.length && courtIds.every((id) => current.courtIds.includes(id));
    const termsChanged =
      draft.date !== current.startAt.slice(0, 10) ||
      draft.startTime !== current.startAt.slice(11, 16) ||
      draft.endTime !== current.endAt.slice(11, 16) ||
      draft.expectedParticipants !== current.expectedParticipants ||
      !sameCourts;
    const planInputChanged = draft.scoring !== current.scoring || draft.pointsPerMatch !== current.pointsPerMatch;
    const viaReschedule = TERMS_OPEN.has(current.status) && (termsChanged || planInputChanged);

    if (!viaReschedule && Object.keys(payload).length === 0) {
      toast.info('Изменений нет');
      setEditing(null);
      return;
    }

    submitEditMut.mutate({ current, draft, courtIds, payload, viaReschedule });
  };

  const value: TournamentPanelContextValue = {
    openCreate,
    openEdit,
    closeEdit,
  };

  return (
    <TournamentPanelContext.Provider value={value}>
      {props.children}
      <CreateTournamentDialog
        open={creating()}
        onClose={() => setCreating(false)}
        onProceed={onCreateProceed}
      />
      <CreateTournamentDialog
        open={!!editing()}
        isEdit
        initialDraft={editing() ? tournamentToDraft(editing()!) : null}
        editTerms={editTerms()}
        submitting={submitEditMut.isPending}
        onClose={closeEdit}
        onProceed={() => {}}
        onSubmitEdit={(draft, courtIds) => {
          void submitEdit(draft, courtIds);
        }}
      />
      <Show when={resetting()}>
        {(t) => (
          <ResetBracketDialog
            tournamentId={t().id}
            tournamentName={t().name}
            onClose={() => setResetting(null)}
          />
        )}
      </Show>
    </TournamentPanelContext.Provider>
  );
};

export const useTournamentPanel = (): TournamentPanelContextValue => {
  const ctx = useContext(TournamentPanelContext);
  if (!ctx)
    throw new Error('useTournamentPanel must be used within <TournamentPanelProvider>');
  return ctx;
};
